// Mocks for /api/secure/me/messages/*

import { delay } from './index'

const STORAGE_KEY = 'patientPortal.mock.messages.v1'

function readStore() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
  } catch (e) {
    return {}
  }
}
function writeStore(s) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
  } catch (e) {
    /* ignore */
  }
}

function seedFor(profile) {
  if (!profile) return { threads: [], history: {} }
  const isSelf = profile.relation === 'SELF'
  const now = Date.now()
  const threads = isSelf
    ? [
        {
          peerId: 'prac_dr_meera',
          peerType: 'PRACTITIONER',
          peerName: 'Dr. Meera Iyer',
          peerSpecialty: 'Therapist',
          lastMessagePreview: 'Glad to hear the sleep changes are sticking. See you tomorrow.',
          lastMessageAt: new Date(now - 2 * 3600 * 1000).toISOString(),
          unreadCount: 1,
          availabilityHint: 'Replies in ~2h',
          pinned: false,
        },
        {
          peerId: 'support',
          peerType: 'SUPPORT',
          peerName: 'AUMHUM Support',
          peerSpecialty: null,
          lastMessagePreview: 'Your invoice for April is ready.',
          lastMessageAt: new Date(now - 30 * 86400000).toISOString(),
          unreadCount: 0,
          availabilityHint: null,
          pinned: true,
        },
      ]
    : [
        {
          peerId: 'prac_dr_rao',
          peerType: 'PRACTITIONER',
          peerName: 'Dr. Rao',
          peerSpecialty: 'Endocrinology',
          lastMessagePreview: 'Bring lab results to the next visit.',
          lastMessageAt: new Date(now - 3 * 86400000).toISOString(),
          unreadCount: 0,
          availabilityHint: 'Replies in ~24h',
          pinned: false,
        },
      ]
  const history = {}
  if (isSelf) {
    history.prac_dr_meera = [
      {
        id: 'm1',
        direction: 'peer',
        type: 'TEXT',
        text: 'How was the week with the new bedtime routine?',
        sentAt: new Date(now - 30 * 3600 * 1000).toISOString(),
        read: true,
      },
      {
        id: 'm2',
        direction: 'patient',
        type: 'TEXT',
        text: 'Lights out by 11 most nights. Sleep is better, mornings still rough.',
        sentAt: new Date(now - 28 * 3600 * 1000).toISOString(),
        read: true,
      },
      {
        id: 'm3',
        direction: 'peer',
        type: 'TEXT',
        text: 'Glad to hear the sleep changes are sticking. See you tomorrow.',
        sentAt: new Date(now - 2 * 3600 * 1000).toISOString(),
        read: false,
      },
    ]
  }
  return { threads, history }
}

function ensure(profile) {
  const store = readStore()
  if (!store[profile.id]) {
    store[profile.id] = seedFor(profile)
    writeStore(store)
  }
  return { store, prof: store[profile.id] }
}

export async function listThreads(profile) {
  await delay()
  if (!profile) return []
  const { prof } = ensure(profile)
  return [...prof.threads].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
    return (b.lastMessageAt || '').localeCompare(a.lastMessageAt || '')
  })
}

export async function getThread(profile, peerId, page = 1, pageSize = 30) {
  await delay()
  if (!profile) return { items: [] }
  const { prof } = ensure(profile)
  const all = prof.history[peerId] || []
  return { items: all, page, pageSize, totalItems: all.length, hasMore: false }
}

export async function send(profile, peerId, body) {
  await delay()
  if (!profile) return null
  const { store, prof } = ensure(profile)
  if (!prof.history[peerId]) prof.history[peerId] = []
  const m = {
    id: `m_${Date.now()}`,
    direction: 'patient',
    type: body.type || 'TEXT',
    text: body.text || null,
    mediaUrl: body.mediaUrl || null,
    mediaMimeType: body.mediaMimeType || null,
    sentAt: new Date().toISOString(),
    read: false,
  }
  prof.history[peerId].push(m)
  // Bump thread row
  const ti = prof.threads.findIndex((t) => t.peerId === peerId)
  if (ti !== -1) {
    prof.threads[ti] = {
      ...prof.threads[ti],
      lastMessagePreview: m.text || (m.type === 'IMAGE' ? '📷 Image' : '📎 File'),
      lastMessageAt: m.sentAt,
    }
  }
  store[profile.id] = prof
  writeStore(store)
  return m
}

export async function markRead(profile, peerId) {
  await delay(60)
  if (!profile) return
  const { store, prof } = ensure(profile)
  const ti = prof.threads.findIndex((t) => t.peerId === peerId)
  if (ti !== -1) {
    prof.threads[ti] = { ...prof.threads[ti], unreadCount: 0 }
  }
  if (prof.history[peerId]) {
    prof.history[peerId] = prof.history[peerId].map((m) => ({ ...m, read: true }))
  }
  store[profile.id] = prof
  writeStore(store)
}

export async function unreadCount(profile) {
  await delay(40)
  if (!profile) return { messages: 0, notifications: 0 }
  const { prof } = ensure(profile)
  const messages = prof.threads.reduce((s, t) => s + (t.unreadCount || 0), 0)
  return { messages, notifications: 0 }
}

export function __resetMessagesMock() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    /* ignore */
  }
}
